"use client";

import React from "react";
import Image from "next/image";
import { stegaClean } from "@sanity/client/stega";
import { PageTrademania2ThankYouSchema } from "@/sanity/pages/PageTrademania2ThankYouTemplate";
import { COUNTDOWN_UNITS, useCountdown } from "@/components/trademania-2/shared";

import colon from "@/public/trademania-2/ty-colon.svg";

// Figma crops the 16:9 helmet still to 112.5% of the 2:1 frame width, centred and anchored at the bottom
const IMAGE_CROP = "absolute bottom-0 left-1/2 aspect-video w-[112.5%] max-w-none -translate-x-1/2 object-cover";

/** Light countdown card (Figma "Countdown"); phones are the desktop card scaled to 85.5%. */
function Countdown({ label, target }: { label?: string; target?: string }) {
  const parts = useCountdown(target);

  return (
    <div className="flex w-full flex-col items-center gap-[2.25rem] rounded-[6px] border border-[#0B0C0D]/[.06] bg-white px-[1.523rem] py-[3rem] backdrop-blur-[26.4px] lg:gap-[2rem] lg:border-[#0B0C0D]/10 lg:grow lg:px-[2.25rem] lg:pb-[2rem] lg:pt-[2.5rem] lg:backdrop-blur-[39px]">
      {label && (
        <p className="whitespace-nowrap font-geist text-[2rem] font-medium leading-[1.185rem] text-[#0B0C0D] lg:text-[2.25rem] lg:leading-[1.75rem]">
          {label}
        </p>
      )}
      <span className="h-px w-full bg-[#0B0C0D]/[.14]" />
      <div className="flex items-start gap-[1.25rem] lg:gap-[1.75rem]">
        {COUNTDOWN_UNITS.map(([key, unit], i) => (
          <React.Fragment key={key}>
            {i > 0 && (
              // eslint-disable-next-line @next/next/no-img-element
              <img
                src={colon.src}
                alt=""
                aria-hidden
                width={5}
                height={46}
                className="h-[4.917rem] w-[.534rem] lg:h-[5.75rem] lg:w-[.625rem]"
              />
            )}
            <div className="flex flex-col items-center gap-[1.069rem] lg:gap-[1.25rem]">
              {/* height = padding + cap height, as Figma trims the digits to their caps */}
              <span className="flex h-[7.91rem] w-[8.979rem] items-center justify-center rounded-[5.131px] border border-[#0B0C0D]/[.04] bg-[#0B0C0D]/[.06] lg:h-[9.25rem] lg:w-[10.5rem] lg:rounded-[6px]">
                <span className="font-bebas text-[6.414rem] leading-none text-[#0B0C0D] [text-box:trim-both_cap_alphabetic] lg:text-[7.5rem]">
                  {parts[key]}
                </span>
              </span>
              <span className="font-geist text-[1.71rem] font-medium leading-normal text-[#0B0C0D]/50 lg:text-[2rem]">
                {unit}
              </span>
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}

/** Figma "Media": looping muted video (poster = image) or the still alone, above the countdown card. */
export default function Media({ data }: { data: PageTrademania2ThankYouSchema }) {
  const image = data?.mediaImage;
  const video = data?.mediaVideo;

  return (
    <div className="flex w-full flex-col gap-[.677rem] lg:w-[73.5rem] lg:shrink-0 lg:gap-[1rem]">
      {(image?.src || video?.url) && (
        <div className="relative aspect-[2/1] w-full overflow-clip rounded-[6px] bg-[#0B0C0D]/[.06]">
          {video?.url ? (
            // fills the frame centred, like the launch page hero it comes from
            <video
              className="absolute inset-0 h-full w-full object-cover"
              src={video.url}
              poster={image?.src}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
            />
          ) : (
            image?.src && (
              <Image
                src={image}
                alt=""
                loading="eager"
                sizes="(max-width: 992px) 113vw, 35vw"
                className={IMAGE_CROP}
              />
            )
          )}
        </div>
      )}
      <Countdown label={data?.eventDateLabel} target={stegaClean(data?.eventDate)} />
    </div>
  );
}
